/**
 * Shared currency handoff for the public anonymous flows (Campaign
 * landing pages + Landing Pages). Kept in one place so both entry
 * points use the exact same resolution rules and share localStorage.
 *
 * Priority when resolving:
 *   1. Valid `?currency=` query param (uppercase ISO 4217 in supported set)
 *   2. Value stored from a previous valid handoff
 *   3. Explicit fallback (e.g. the Landing Page's `default_currency`)
 *   4. EUR
 *
 * Invalid `?currency=` values fall through to step 2/3/4 with a
 * console.warn so a marketing-site typo never blows up the page.
 *
 * A valid query-param value is persisted to localStorage so it
 * survives reload + login redirect roundtrips. The storage key is
 * shared with Campaigns for backwards compatibility.
 */

// Must mirror `useCurrency.js` symbol table + the backend ECB rate list.
// Extended beyond the original Campaign-only set (EUR/DKK/SEK/NOK) to
// also cover USD/GBP because Landing Pages accept the marketing site's
// full currency list.
export const SUPPORTED_PUBLIC_CURRENCIES = ['EUR', 'DKK', 'SEK', 'NOK', 'USD', 'GBP'];

// Kept as `flow_campaign_currency` (not `flow_public_currency`) so
// existing browser sessions with a stored campaign preference still
// pick up the correct value after this refactor. Landing Pages reuse
// the same key — one anonymous visitor, one currency preference.
const STORAGE_KEY = 'flow_campaign_currency';

/**
 * @param {string|null} rawParam  Value of `?currency=` (may be null/undefined)
 * @param {string} [fallback='EUR']  Default when no valid param + no storage hit
 * @returns {string}  ISO 4217 code guaranteed to be in `SUPPORTED_PUBLIC_CURRENCIES`
 */
export function resolvePublicCurrency(rawParam, fallback = 'EUR') {
    if (rawParam) {
        const upper = String(rawParam).toUpperCase();
        if (SUPPORTED_PUBLIC_CURRENCIES.includes(upper)) {
            try { localStorage.setItem(STORAGE_KEY, upper); } catch { /* ignore */ }
            return upper;
        }
        // eslint-disable-next-line no-console
        console.warn(
            `[PublicCurrency] Unsupported ?currency=${rawParam}; falling back. `
            + `Supported: ${SUPPORTED_PUBLIC_CURRENCIES.join(', ')}`
        );
    }
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored && SUPPORTED_PUBLIC_CURRENCIES.includes(stored)) return stored;
    } catch { /* ignore */ }
    const fb = String(fallback || 'EUR').toUpperCase();
    return SUPPORTED_PUBLIC_CURRENCIES.includes(fb) ? fb : 'EUR';
}
